import { useState } from 'react';
import { Modal, Button, Card } from '@rackd/ui';
import {
  IconAlertTriangle,
  IconBrandApple,
  IconBrandGoogle,
  IconCreditCard,
  IconCheck,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { CancelSubscriptionResult } from '../api';

interface CancelSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string | null;
  platform: 'ios' | 'android' | 'web' | null;
  subscriptionExpiresAt: Date | null;
  onCancel: (cancelAtPeriodEnd: boolean) => Promise<{ success: boolean; data: CancelSubscriptionResult }>;
  isCancelling: boolean;
  result: CancelSubscriptionResult | null;
}

const platformInfo = {
  ios: {
    icon: IconBrandApple,
    name: 'App Store',
    color: 'text-[var(--text)]',
    canCancel: false,
  },
  android: {
    icon: IconBrandGoogle,
    name: 'Google Play',
    color: 'text-[#4285F4]',
    canCancel: true,
  },
  web: {
    icon: IconCreditCard,
    name: 'Stripe',
    color: 'text-[#635BFF]',
    canCancel: true,
  },
};

export function CancelSubscriptionModal({
  open,
  onClose,
  userName,
  userEmail,
  platform,
  subscriptionExpiresAt,
  onCancel,
  isCancelling,
  result,
}: CancelSubscriptionModalProps) {
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);

  const platformData = platform ? platformInfo[platform] : null;
  const PlatformIcon = platformData?.icon;

  const handleCancel = async () => {
    // Always call the RPC even for iOS - backend will:
    // 1. Log an audit event for the cancellation attempt
    // 2. Return 'unsupported' status with appropriate message for iOS
    // 3. Handle actual cancellation for Android/Web platforms
    await onCancel(cancelAtPeriodEnd);
  };

  const handleClose = () => {
    onClose();
  };

  // Result view after cancellation attempt
  if (result) {
    const isSuccess = result.status === 'success';
    const isUnsupported = result.status === 'unsupported';

    return (
      <Modal
        open={open}
        onClose={handleClose}
        title={isSuccess ? 'Subscription Cancelled' : isUnsupported ? 'Action Required' : 'Cancellation Failed'}
        size="md"
        footer={
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Status indicator */}
          <div
            className={`flex items-center gap-3 p-4 rounded-lg ${
              isSuccess
                ? 'bg-[var(--success)]/10'
                : isUnsupported
                  ? 'bg-[var(--warning)]/10'
                  : 'bg-[var(--error)]/10'
            }`}
          >
            {isSuccess ? (
              <IconCheck size={24} className="text-[var(--success)]" />
            ) : isUnsupported ? (
              <IconInfoCircle size={24} className="text-[var(--warning)]" />
            ) : (
              <IconX size={24} className="text-[var(--error)]" />
            )}
            <div>
              <p className="font-medium text-[var(--text)]">
                {isSuccess
                  ? 'Subscription has been cancelled'
                  : isUnsupported
                    ? 'Cannot cancel via admin'
                    : 'Failed to cancel subscription'}
              </p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{result.message}</p>
            </div>
          </div>

          {/* Platform info */}
          {result.platform && (
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              {platformInfo[result.platform]?.icon && (
                <span className={platformInfo[result.platform].color}>
                  {(() => {
                    const Icon = platformInfo[result.platform].icon;
                    return <Icon size={16} />;
                  })()}
                </span>
              )}
              Platform: {platformInfo[result.platform]?.name}
            </div>
          )}

          {/* Effective end date */}
          {result.effective_end && (
            <div className="text-sm text-[var(--text-secondary)]">
              Access ends:{' '}
              <span className="font-medium text-[var(--text)]">
                {new Date(result.effective_end).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* iOS specific instructions */}
          {isUnsupported && result.platform === 'ios' && (
            <Card className="p-4 bg-[var(--background-tertiary)]">
              <h4 className="font-medium text-[var(--text)] mb-2">User Instructions</h4>
              <p className="text-sm text-[var(--text-secondary)]">
                The user must cancel their subscription directly in the App Store:
              </p>
              <ol className="text-sm text-[var(--text-secondary)] mt-2 ml-4 list-decimal space-y-1">
                <li>Open the Settings app</li>
                <li>Tap their name at the top</li>
                <li>Tap &quot;Subscriptions&quot;</li>
                <li>Find and tap &quot;Rackd&quot;</li>
                <li>Tap &quot;Cancel Subscription&quot;</li>
              </ol>
            </Card>
          )}
        </div>
      </Modal>
    );
  }

  // Initial view - confirmation
  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Cancel Subscription"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isCancelling}>
            Back
          </Button>
          <Button variant="danger" onClick={handleCancel} loading={isCancelling}>
            {platform === 'ios' ? 'Check Status' : 'Cancel Subscription'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Warning */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-[var(--warning)]/10">
          <IconAlertTriangle size={24} className="text-[var(--warning)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[var(--text)]">Confirm subscription cancellation</p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              You are about to cancel the subscription for this user. This action may not be
              reversible.
            </p>
          </div>
        </div>

        {/* User info */}
        <div className="p-4 rounded-lg bg-[var(--background-tertiary)]">
          <p className="text-sm text-[var(--text-tertiary)]">User</p>
          <p className="font-medium text-[var(--text)]">{userName || 'No name'}</p>
          <p className="text-sm text-[var(--text-secondary)]">{userEmail || 'No email'}</p>
        </div>

        {/* Platform info */}
        {platformData && PlatformIcon && (
          <div className="p-4 rounded-lg border border-[var(--border)]">
            <div className="flex items-center gap-2 mb-2">
              <PlatformIcon size={20} className={platformData.color} />
              <span className="font-medium text-[var(--text)]">{platformData.name}</span>
            </div>
            {!platformData.canCancel && (
              <p className="text-sm text-[var(--warning)]">
                App Store subscriptions cannot be cancelled via admin. The user must cancel directly
                in their App Store settings.
              </p>
            )}
            {platformData.canCancel && subscriptionExpiresAt && (
              <p className="text-sm text-[var(--text-secondary)]">
                Current period ends:{' '}
                {subscriptionExpiresAt.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}
          </div>
        )}

        {/* Cancel options (only for platforms that support API cancellation) */}
        {platformData?.canCancel && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-[var(--text)]">Cancellation timing</p>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-[var(--border)] cursor-pointer hover:border-[var(--accent-primary)]/30">
              <input
                type="radio"
                name="cancelTiming"
                checked={cancelAtPeriodEnd}
                onChange={() => setCancelAtPeriodEnd(true)}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-[var(--text)]">At period end (Recommended)</p>
                <p className="text-sm text-[var(--text-secondary)]">
                  User keeps access until{' '}
                  {subscriptionExpiresAt?.toLocaleDateString() || 'end of current period'}
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-[var(--border)] cursor-pointer hover:border-[var(--accent-primary)]/30">
              <input
                type="radio"
                name="cancelTiming"
                checked={!cancelAtPeriodEnd}
                onChange={() => setCancelAtPeriodEnd(false)}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-[var(--text)]">Immediately</p>
                <p className="text-sm text-[var(--text-secondary)]">
                  User loses access right away. May issue a prorated refund.
                </p>
              </div>
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
